import { Ingredients } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /ingredients/* — глобальный
 * платформенный пул (cafeId IS NULL). Ответ листинга — admin-проекция
 * (с costPerUnit). Все ответы парсятся через Zod (drift-guard). DELETE
 * отдаёт HTTP 204 без тела — парсинг body пропускаем (api-contracts.md).
 */
export const ingredientsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listIngredients: build.query<
            Ingredients.GetIngredientsResponse,
            Ingredients.GetIngredientsRequest | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                if (params?.groupId) {
                    search.groupId = params.groupId;
                }
                if (params?.q) {
                    search.q = params.q;
                }
                if (params?.isActive !== undefined) {
                    search.isActive = params.isActive ? 'true' : 'false';
                }
                if (params?.allergen) {
                    search.allergen = params.allergen;
                }
                return {
                    url: '/ingredients',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): Ingredients.GetIngredientsResponse =>
                parseWithSchema(Ingredients.GetIngredientsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((i) => ({
                            type: 'Ingredient' as const,
                            id: i.id
                        })),
                        { type: 'Ingredient' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Ingredient' as const, id: 'LIST' }]
        }),

        createIngredient: build.mutation<
            Ingredients.CreateIngredientResponse,
            Ingredients.CreateIngredientRequest
        >({
            query: (body) => ({
                url: '/ingredients',
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): Ingredients.CreateIngredientResponse =>
                parseWithSchema(Ingredients.CreateIngredientResponseSchema, raw),
            invalidatesTags: [{ type: 'Ingredient', id: 'LIST' }]
        }),

        updateIngredient: build.mutation<
            Ingredients.UpdateIngredientResponse,
            { id: string; body: Ingredients.UpdateIngredientRequest }
        >({
            query: ({ id, body }) => ({
                url: `/ingredients/${id}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (raw: unknown): Ingredients.UpdateIngredientResponse =>
                parseWithSchema(Ingredients.UpdateIngredientResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Ingredient', id: arg.id },
                { type: 'Ingredient', id: 'LIST' }
            ]
        }),

        deleteIngredient: build.mutation<Ingredients.DeleteIngredientResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/ingredients/${id}`,
                method: 'DELETE'
            }),
            // HTTP 204 — тела нет, парсить нечего (api-contracts.md).
            transformResponse: (): Ingredients.DeleteIngredientResponse => ({}),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Ingredient', id: arg.id },
                { type: 'Ingredient', id: 'LIST' }
            ]
        })
    })
});

export const {
    useListIngredientsQuery,
    useCreateIngredientMutation,
    useUpdateIngredientMutation,
    useDeleteIngredientMutation
} = ingredientsApi;
